import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import { useRunnerState } from '@/composables/useRunnerState'
import { getContentfulUrl } from '@/utils/contentful'
import { CollectibleEntity, CollectStatus } from '@/webgl/runner/entities/CollectibleEntity'
import { ShoppingMode } from '@/webgl/runner/entities/ShoppingEntity'

import RunnerShoppingCartItem from './RunnerShoppingCartItem'

type RunnerShoppingCounterProps = {
  fixed: boolean
  value?: number
  label: string
}

export default function RunnerShoppingCounter(props: RunnerShoppingCounterProps) {
  const { gsap } = useGSAP()
  const { t } = useTranslation()
  const { shoppingState, persona, scenario, gameState } = useRunnerState()

  const betterFormat = useImageFormatContext()
  const [coinUrl, setCoinUrl] = useState(null)
  const [freezeUrl, setFreezeUrl] = useState(null)
  useEffect(() => {
    setCoinUrl(getContentfulUrl(`/src/assets/runner/coin_03.png`, { w: 188, fm: betterFormat }))
    setFreezeUrl(getContentfulUrl(`/src/assets/runner/freeze.png`, { w: 188, fm: betterFormat }))
  }, [betterFormat])

  const isShopping = shoppingState?.mode == ShoppingMode.Shop
  const isFreezing = shoppingState?.freezeActive

  const amountCart = shoppingState?.cartValue
  const [amountCartValue, setAmountCartValue] = useState<number>(0)
  const color = shoppingState?.mode == ShoppingMode.Balance ? 'bg-coral' : 'bg-cream'

  const elementRef = useRef<HTMLDivElement>(null)
  const amountRef = useRef<HTMLDivElement>(null)
  const amoutBackgroundRef = useRef<HTMLDivElement>(null)
  const amountCartRef = useRef<HTMLDivElement>(null)
  const amountCartReveal = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(true)
  const [isRevealing, setIsRevealing] = useState(false)
  const [calculation, setCalculation] = useState(0)
  const [isCalculated, setIsCalculated] = useState(false)
  const [counterColor, setCounterColor] = useState('bg-cream')

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (isVisible) {
        const timeline = gsap.timeline({ onComplete: () => setIsRevealing(false) })

        if (isShopping) {
          setIsCalculated(false)

          timeline.set(amountCartReveal.current, { opacity: 0 }, 0)
          timeline.set(amountCartReveal.current, { yPercent: 0 }, 0)
        } else {
          setIsRevealing(true)

          timeline.to(
            [amountCartReveal.current],
            {
              yPercent: 20,
              opacity: 1,
              duration: 0.7,
              ease: 'back.out(1.2)',
            },
            0
          )
          // return;
          timeline.addLabel('calculation', 1)
          const proxy = {
            value: 0,
          }
          timeline.add(() => {
            setCalculation(0)
          }, 'calculation')
          timeline.fromTo(
            proxy,
            {
              value: 0,
            },
            {
              value: amountCart,
              duration: 0.5,
              ease: 'power1.inOut',
              onUpdate: () => {
                setCalculation(Math.round(proxy.value * 10) / 10)
              },
            }
          )
          timeline.to(
            amountCartReveal.current,
            {
              onStart: () => {
                setCounterColor('bg-coral')
              },
              yPercent: -20,
              opacity: 0,
              duration: 1,
              ease: 'quart.out',
            },
            'calculation'
          )

          timeline.add(() => {
            setIsCalculated(true)
          }, 'calculation+=1')
        }
      }
    })
    return () => ctx.kill()
    // eslint-disable-next-line
  }, [isShopping, gsap, setCalculation, setIsCalculated, amountRef, amountCartReveal, amountCartRef, setIsRevealing])

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (isShopping) {
        const proxy = {
          value: amountCartValue,
        }
        gsap
          .timeline({
            onUpdate: () => {
              setAmountCartValue(Math.round(proxy.value * 10) / 10)
            },
          })
          .fromTo(
            proxy,
            {
              value: amountCartValue,
            },
            {
              value: amountCart,
              duration: 0.2,
              ease: 'power1.out',
            },
            0.5
          )
      }
    })

    return () => ctx.kill()
    // eslint-disable-next-line
  }, [amountCart, setAmountCartValue])

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (!isShopping && !isRevealing) {
        const proxy = {
          value: calculation,
        }
        gsap
          .timeline({
            onUpdate: () => {
              setCalculation(Math.round(proxy.value * 10) / 10)
            },
          })
          .fromTo(
            proxy,
            {
              value: calculation,
            },
            {
              value: amountCart,
              duration: 0.2,
              ease: 'power1.out',
            },
            0
          )
      }
    })

    return () => ctx.kill()
    // eslint-disable-next-line
  }, [amountCart, setCalculation, isRevealing])

  // const lastItem = shoppingState?.cart ? (shoppingState?.cart[shoppingState?.cart.length - 1]) : null;
  const nbItems = shoppingState?.cart ? shoppingState?.cart.length : 0
  const [cart, setCart] = useState<
    Array<{
      item: CollectibleEntity
      isInCart: boolean
    }>
  >([])

  useLayoutEffect(() => {
    if (isShopping) {
      setCart(shoppingState?.cart.map((item) => ({ item, isInCart: true })) || [])
    } else {
      // Remove items from cart
      setCart((items) => {
        items.forEach((element) => {
          element.isInCart = !!shoppingState?.cart.find((cartItem) => cartItem.uuid === element.item.uuid)
        })

        return items
      })
      // setCart(shoppingState?.cart.map((item) => ({item, isInCart: true})) || []);
    }
    // eslint-disable-next-line
  }, [nbItems, setCart])

  // RESET CART ON BALANCE
  useLayoutEffect(() => {
    if (!isShopping) {
      setCart(shoppingState?.cart.map((item) => ({ item, isInCart: true })) || [])
    }
    // eslint-disable-next-line
  }, [isShopping, setCart])

  // WIGGLE ANIMATION
  useLayoutEffect(() => {
    if (gameState.collectStatus !== CollectStatus.Neutral) {
      if (elementRef.current) {
        gsap
          .timeline()
          .addLabel('start', isShopping ? 0.5 : 0)
          .fromTo(
            elementRef.current,
            {
              x: 0,
              y: 0,
            },
            {
              duration: 0.1,
              x: 0,
              y: -5,
              ease: 'none',
            },
            'start'
          )
          .to(
            elementRef.current,
            {
              duration: 0.1,
              x: 0,
              y: 5,
              ease: 'none',
            },
            'start+=0.1'
          )
          .to(
            elementRef.current,
            {
              duration: 0.1,
              x: 0,
              y: 0,
              ease: 'none',
            },
            'start+=0.2'
          )
      }
    }

    // eslint-disable-next-line
  }, [elementRef, gameState.collectStatus, gsap])

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (isCalculated) {
        gsap
          .timeline({ delay: 2, repeat: -1, repeatDelay: 1.6 })
          .fromTo(
            amoutBackgroundRef.current,
            {
              opacity: 1,
            },
            {
              opacity: 0.7,
              duration: 0.1,
              ease: 'power1.out',
            },
            0
          )
          .to(
            amoutBackgroundRef.current,
            {
              opacity: 1,
              duration: 0.1,
              ease: 'power1.out',
            },
            0.1
          )
          .to(
            amoutBackgroundRef.current,
            {
              opacity: 0.7,
              duration: 0.1,
              ease: 'power1.out',
            },
            0.2
          )
          .to(
            amoutBackgroundRef.current,
            {
              opacity: 1,
              duration: 0.1,
              ease: 'power1.out',
            },
            0.3
          )
      }
    })

    return () => ctx.revert()
  }, [isCalculated, amoutBackgroundRef, gsap])

  return (
    <div ref={elementRef} className={`RunnerShoppingCart w-[84px] self-start relative`}>
      {cart.map((item, index) => (
        <RunnerShoppingCartItem key={item.item.uuid} item={item.item} isInCart={item.isInCart} index={index} />
      ))}
      <div className='absolute top-0 left-0 w-full h-full bg-secondary rounded-20' />
      <div className='relative w-full rounded-16 p-2'>
        <div className='relative text-body-s grid overflow-hidden text-center'>
          <div
            className={`col-start-1 col-span-1 row-start-1 row-span-1 px-14 pt-10 transition-transform duration-700 ease-out-back`}
          >
            {props.label}
          </div>
        </div>
        <img
          src={coinUrl}
          className={`absolute pointer-events-none w-96 -top-40 -right-24 transition-all duration-700 ease-out-back ${isFreezing ? 'translate-y-1/2 opacity-0' : isShopping ? '-translate-y-1/2 opacity-0' : ''}`}
        />
        <img
          src={freezeUrl}
          className={`absolute pointer-events-none w-96 -top-40 -right-24 transition-all duration-700 ease-out-back ${isFreezing ? '' : '-translate-y-1/2 opacity-0'}`}
        />

        <div className={`relative text-numbers text-24 rounded-16 text-center border-2 border-dark-cream`}>
          <div
            ref={amountRef}
            className={`relative py-5 px-6 overflow-visible rounded-16  ${!isShopping ? counterColor : 'bg-cream'} transition-colors duration-500 ease-out-quad`}
          >
            <div ref={amoutBackgroundRef} className={`absolute top-0 left-0 w-full h-full`} />
            <div ref={amountCartRef} className='relative z-1'>
              {isShopping ? `???` : `$ ${calculation}`}
            </div>
          </div>
          <div
            className={`absolute z-2 top-0 left-0 w-full h-full bg-light-blue rounded-16 transition-opacity duration-500 ease-out-quad ${isFreezing ? '' : 'opacity-0'}`}
          />
          <svg
            className={`absolute z-2 bottom-0 w-full transition-transform duration-1000 delay-300 ease-out-quad ${isFreezing ? 'translate-y-1/2' : 'scale-y-0'}`}
            viewBox='0 0 143 46'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M127.917 26.3283C126.539 37.1927 121.97 44.5099 117.711 42.6717C113.453 40.8335 111.117 30.5361 112.495 19.6717C113.873 8.80732 118.442 1.49013 122.701 3.32829C126.959 5.16645 129.295 15.4639 127.917 26.3283Z'
              fill='#4EA4BD'
            />
            <path
              d='M123.428 33.3523C122.282 36.8001 120.379 39.1829 119.178 38.6744C117.978 38.1659 117.934 34.9586 119.081 31.5108C120.227 28.063 122.13 25.6802 123.331 26.1887C124.531 26.6972 124.575 29.9045 123.428 33.3523Z'
              fill='#FFF4E5'
            />
            <path
              d='M112.775 27.6576C112.099 32.9913 109.412 36.3923 106.775 35.254C104.138 34.1156 102.548 28.869 103.225 23.5352C103.901 18.2015 106.587 14.8005 109.225 15.9389C111.862 17.0772 113.452 22.3239 112.775 27.6576Z'
              fill='#4EA4BD'
            />
            <path
              d='M27.6712 25.5442C27.0089 30.7665 24.3517 34.0848 21.7361 32.9558C19.1205 31.8268 17.5371 26.6781 18.1995 21.4558C18.8618 16.2335 21.5191 12.9152 24.1346 14.0442C26.7502 15.1732 28.3336 20.3219 27.6712 25.5442Z'
              fill='#4EA4BD'
            />
          </svg>

          <div className='absolute z-2 top-0 left-0 w-full h-full flex items-center justify-center text-cream'>
            <div className='relative overflow-hidden'>
              <div
                className={`transition-transform duration-700 ease-out-back ${isFreezing ? '' : 'translate-y-full'}`}
              >
                {t('misc.freeze')}
              </div>
            </div>
          </div>
        </div>

        <div
          ref={amountCartReveal}
          className={`absolute left-4 right-4 text-numbers text-24 rounded-16 text-center overflow-hidden`}
        >
          <div
            className={`relative px-5 py-5 overflow-visible rounded-16 bg-coral transition-colors duration-500 ease-out-quad`}
          >
            <div className='relative z-1'>{`$ ${amountCart}`}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
